import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import axios from '../lib/axios';
import { socket, connectSocket } from '../lib/socket';
import { 
  Home, MessageSquare, Send, Check, LogOut, Phone, PhoneOff, X, ArrowLeft, Plus,
  Trophy, TrendingUp, Zap, Crown, Shield, DollarSign, Activity, Flame, 
  Sparkles, ChevronRight, Bell, Timer, Gamepad2, Wallet, User, Swords, 
  Gem, Coins, ShieldAlert, AlertCircle, Play, UserPlus, ChevronDown, Minus,
  ArrowDownRight, Tag, Reply, Smile, Globe, CheckCheck, BarChart3,
  Award, ArrowUpRight, Download, Copy, TrendingDown, Clock, UserCheck
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { formatCurrency, formatDateTime, formatRelativeTime } from '../lib/utils';
import { PlayerSearchInput } from '../components/shared/PlayerSearchInput';

// ============ GAMING THEME COLORS ============
const THEME = {
  bg: '#050508',
  bgGradient: 'linear-gradient(135deg, #0a0a0f 0%, #0d0d18 50%, #0a0a0f 100%)',
  card: 'rgba(15, 15, 25, 0.9)',
  cardBorder: 'rgba(147, 51, 234, 0.3)',
  neonCyan: '#00F5FF',
  neonPurple: '#9945FF',
  neonGold: '#FFD700',
  neonGreen: '#00FF88',
  neonPink: '#FF3366',
  neonOrange: '#FF6B00',
};

// ============ ANIMATED BACKGROUND ============
function GamingBackground() {
  return (
    <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0" style={{ background: THEME.bgGradient }} />
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: `linear-gradient(${THEME.neonPurple}20 1px, transparent 1px), linear-gradient(90deg, ${THEME.neonPurple}20 1px, transparent 1px)`, backgroundSize: '50px 50px' }} />
      {[...Array(15)].map((_, i) => (
        <motion.div key={i} className="absolute w-1 h-1 rounded-full" style={{ background: [THEME.neonCyan, THEME.neonPurple, THEME.neonGold][i % 3], left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }} animate={{ y: [0, -30, 0], opacity: [0.2, 0.8, 0.2] }} transition={{ duration: 3 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }} />
      ))}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${THEME.neonPurple}40 0%, transparent 70%)` }} />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${THEME.neonCyan}40 0%, transparent 70%)` }} />
    </div>
  );
}

// ============ LEVEL UP ANIMATION ============
function LevelUpOverlay({ oldLevel, newLevel, tierName, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  const tierColor = newLevel >= 8 ? THEME.neonGold : newLevel >= 5 ? THEME.neonPurple : THEME.neonCyan;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[250] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/95 backdrop-blur-md" />
      {[...Array(50)].map((_, i) => (<motion.div key={i} initial={{ x: '50vw', y: '50vh', scale: 0, opacity: 1 }} animate={{ x: `${50 + (Math.random() - 0.5) * 100}vw`, y: `${50 + (Math.random() - 0.5) * 100}vh`, scale: [0, 2, 0], opacity: [1, 1, 0] }} transition={{ duration: 1.5, ease: 'easeOut' }} className="absolute w-2 h-2 rounded-full" style={{ backgroundColor: tierColor }} />))}
      <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', damping: 10, delay: 0.2 }} className="relative text-center z-10">
        <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: 3, duration: 0.3 }} className="w-32 h-32 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${tierColor}, ${THEME.neonOrange})`, boxShadow: `0 0 80px ${tierColor}` }}>
          {newLevel >= 8 ? <Crown className="w-16 h-16 text-black" /> : newLevel >= 5 ? <Gem className="w-16 h-16 text-black" /> : <Shield className="w-16 h-16 text-black" />}
        </motion.div>
        <motion.h1 initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }} className="text-5xl font-black mb-2" style={{ color: tierColor, textShadow: `0 0 30px ${tierColor}` }}>LEVEL UP!</motion.h1>
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.6, type: 'spring' }} className="flex items-center justify-center gap-4 text-4xl font-black">
          <span className="text-gray-500">LV.{oldLevel}</span><motion.span animate={{ x: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 0.5 }} style={{ color: THEME.neonGold }}>→</motion.span><span style={{ color: tierColor }}>LV.{newLevel}</span>
        </motion.div>
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.8 }} className="mt-4 text-2xl font-black" style={{ color: tierColor }}>{tierName}</motion.div>
      </motion.div>
    </motion.div>
  );
}

// ============ QUICK POPUP (LET'S GO / BYE BYE) - Shows only once ============
function QuickPopup({ message, icon: Icon, color, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 1200); return () => clearTimeout(t); }, [onClose]);
  return (
    <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.5 }} transition={{ duration: 0.15 }} className="fixed inset-0 z-[200] flex items-center justify-center pointer-events-none">
      <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 0.2, repeat: 2 }} className="text-center">
        <motion.div className="w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${color}, ${THEME.neonOrange})`, boxShadow: `0 0 50px ${color}` }}><Icon className="w-12 h-12 text-black" /></motion.div>
        <h1 className="text-4xl font-black" style={{ color, textShadow: `0 0 20px ${color}` }}>{message}</h1>
      </motion.div>
    </motion.div>
  );
}

// ============ APPROVAL NOTIFICATION (Real-time) ============
function ApprovalNotification({ transaction, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); if ('vibrate' in navigator) navigator.vibrate([200, 100, 200]); return () => clearTimeout(t); }, [onClose]);
  return (
    <motion.div initial={{ y: -100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -100, opacity: 0 }} className="fixed top-4 left-4 right-4 z-[150]">
      <Card className="p-4 rounded-2xl" style={{ background: `linear-gradient(135deg, ${THEME.neonGreen}20, ${THEME.neonCyan}10)`, border: `2px solid ${THEME.neonGreen}`, boxShadow: `0 0 30px ${THEME.neonGreen}50` }}>
        <div className="flex items-center gap-3">
          <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: 3, duration: 0.3 }} className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: THEME.neonGreen }}><Check className="w-6 h-6 text-black" /></motion.div>
          <div className="flex-1"><p className="font-black text-white">CONFIRMED BY ADMIN!</p><p className="text-sm text-gray-400">{transaction.player_name} - {formatCurrency(transaction.deposit_amount || transaction.amount)}</p></div>
        </div>
      </Card>
    </motion.div>
  );
}

// ============ PENDING ORDER DETAILS MODAL ============
function PendingOrdersModal({ orders, onClose }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} onClick={e => e.stopPropagation()} className="relative w-full max-w-md rounded-2xl overflow-hidden" style={{ background: THEME.card, border: `2px solid ${THEME.neonOrange}50` }}>
        <div className="p-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${THEME.cardBorder}` }}>
          <div className="flex items-center gap-2"><AlertCircle className="w-5 h-5" style={{ color: THEME.neonOrange }} /><h2 className="font-black text-white">PENDING ORDERS</h2></div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <ScrollArea className="max-h-[60vh]">
          <div className="p-4 space-y-3">
            {orders.map((order, idx) => (
              <div key={order.id || idx} className="p-4 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)', border: `1px solid ${THEME.neonOrange}30` }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-white">{order.player_name}</span>
                  <span className="text-xl font-black" style={{ color: THEME.neonGold }}>{formatCurrency(order.deposit_amount)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">{order.payment_source || order.game_name}</span>
                  <Badge style={{ background: `${THEME.neonOrange}20`, color: THEME.neonOrange }}>Pending</Badge>
                </div>
                <p className="text-xs text-gray-500 mt-2">{formatDateTime(order.created_at)}</p>
              </div>
            ))}
            {orders.length === 0 && <p className="text-center text-gray-500 py-8">No pending orders</p>}
          </div>
        </ScrollArea>
      </motion.div>
    </motion.div>
  );
}

// ============ ADD PLAYER MODAL ============
function AddPlayerModal({ initialName, onClose, onSuccess }) {
  const [name, setName] = useState(initialName || '');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post('/players', { name, phone, wallet_balance: 0 });
      toast.success(`Player "${name}" added!`);
      onSuccess(name);
      onClose();
    } catch (err) {
      toast.error('Failed to add player');
    }
    setLoading(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[110] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} onClick={e => e.stopPropagation()} className="relative w-full max-w-sm rounded-2xl overflow-hidden" style={{ background: THEME.card, border: `2px solid ${THEME.neonCyan}50` }}>
        <div className="p-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${THEME.cardBorder}` }}>
          <div className="flex items-center gap-2"><UserPlus className="w-5 h-5" style={{ color: THEME.neonCyan }} /><h2 className="font-black text-white">ADD NEW PLAYER</h2></div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <Label className="text-xs text-gray-400 mb-2">Player Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Enter name" required className="h-12 rounded-xl border-0" style={{ background: 'rgba(0,0,0,0.3)', color: 'white' }} />
          </div>
          <div>
            <Label className="text-xs text-gray-400 mb-2">Phone (Optional)</Label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone number" className="h-12 rounded-xl border-0" style={{ background: 'rgba(0,0,0,0.3)', color: 'white' }} />
          </div>
          <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl font-black text-black" style={{ background: `linear-gradient(135deg, ${THEME.neonCyan}, ${THEME.neonGreen})` }}>{loading ? 'Adding...' : 'ADD PLAYER'}</Button>
        </form>
      </motion.div>
    </motion.div>
  );
}

// ============ XP LEVEL BADGE ============
function LevelBadge({ level, xp, maxXp }) {
  const tierColor = level >= 8 ? THEME.neonGold : level >= 5 ? THEME.neonPurple : THEME.neonCyan;
  const tierName = level >= 8 ? 'LEGENDARY' : level >= 5 ? 'EPIC' : 'WARRIOR';
  return (
    <motion.div className="relative" whileHover={{ scale: 1.05 }}>
      {level >= 5 && <motion.div animate={{ boxShadow: [`0 0 20px ${tierColor}40`, `0 0 40px ${tierColor}60`, `0 0 20px ${tierColor}40`] }} transition={{ repeat: Infinity, duration: 2 }} className="absolute inset-0 rounded-2xl" />}
      <div className="relative px-4 py-2 rounded-2xl border-2" style={{ background: `linear-gradient(135deg, ${tierColor}20, transparent)`, borderColor: tierColor }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${tierColor}30` }}>
            {level >= 8 ? <Crown className="w-5 h-5" style={{ color: tierColor }} /> : level >= 5 ? <Gem className="w-5 h-5" style={{ color: tierColor }} /> : <Shield className="w-5 h-5" style={{ color: tierColor }} />}
          </div>
          <div>
            <div className="flex items-center gap-2"><span className="text-2xl font-black" style={{ color: tierColor }}>LV.{level}</span><Badge className="text-[10px] px-2 py-0 font-bold" style={{ background: `${tierColor}30`, color: tierColor, border: `1px solid ${tierColor}50` }}>{tierName}</Badge></div>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-24 h-1.5 rounded-full overflow-hidden" style={{ background: `${tierColor}20` }}><motion.div initial={{ width: 0 }} animate={{ width: `${(xp / maxXp) * 100}%` }} className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${tierColor}, ${THEME.neonOrange})` }} /></div>
              <span className="text-[10px] font-mono" style={{ color: tierColor }}>{xp}/{maxXp}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ============ STAT CARD ============
function StatCard({ icon: Icon, label, value, color, suffix = '' }) {
  return (
    <motion.div whileHover={{ scale: 1.02, y: -2 }} className="relative p-4 rounded-2xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${color}15, ${color}05)`, border: `1px solid ${color}30` }}>
      <div className="absolute top-0 right-0 w-20 h-20 rounded-full opacity-10" style={{ background: color, filter: 'blur(20px)' }} />
      <div className="relative"><div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4" style={{ color }} /><span className="text-xs uppercase tracking-wider text-gray-400">{label}</span></div><p className="text-2xl font-black" style={{ color }}>{value}{suffix}</p></div>
    </motion.div>
  );
}

// ============ CELEBRATION OVERLAY ============
function CelebrationOverlay({ amount, playerName, xpGained, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />
      {[...Array(50)].map((_, i) => (<motion.div key={i} initial={{ x: '50vw', y: '50vh', scale: 0 }} animate={{ x: `${Math.random() * 100}vw`, y: `${Math.random() * 100}vh`, scale: [0, 1, 0.5], rotate: Math.random() * 720 }} transition={{ duration: 1.2 + Math.random(), ease: 'easeOut' }} className="absolute w-3 h-3 rounded-full" style={{ backgroundColor: [THEME.neonGold, THEME.neonCyan, THEME.neonPink, THEME.neonGreen][Math.floor(Math.random() * 4)] }} />))}
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 12 }} className="relative text-center z-10">
        <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: 2, duration: 0.4 }} className="w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${THEME.neonGold}, ${THEME.neonOrange})`, boxShadow: `0 0 60px ${THEME.neonGold}` }}><Trophy className="w-12 h-12 text-black" /></motion.div>
        <h1 className="text-4xl font-black mb-2" style={{ background: `linear-gradient(135deg, ${THEME.neonGold}, ${THEME.neonOrange})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>VICTORY!</h1>
        <p className="text-3xl font-bold text-white mb-2">{formatCurrency(amount)}</p>
        <p className="text-gray-400 mb-3">Deposit for <span className="text-white font-bold">{playerName}</span></p>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: `${THEME.neonGreen}20`, border: `1px solid ${THEME.neonGreen}50` }}><Sparkles className="w-5 h-5" style={{ color: THEME.neonGreen }} /><span className="text-xl font-black" style={{ color: THEME.neonGreen }}>+{xpGained} XP</span></div>
      </motion.div>
    </motion.div>
  );
}

// ============ ADMIN PING ALERT ============
function AdminPingAlert({ ping, onAcknowledge }) {
  const audioRef = useRef(null);
  useEffect(() => { const audio = audioRef.current; if (ping) { try { audio?.play(); } catch (e) {} if ('vibrate' in navigator) navigator.vibrate([500, 200, 500, 200, 500]); } return () => audio?.pause(); }, [ping]);
  if (!ping) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[300] flex items-center justify-center p-6">
      <motion.div animate={{ backgroundColor: [`${THEME.neonPink}30`, `${THEME.neonOrange}30`, `${THEME.neonPink}30`] }} transition={{ repeat: Infinity, duration: 0.5 }} className="absolute inset-0" />
      <audio ref={audioRef} loop src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1f" />
      <motion.div initial={{ scale: 0.5 }} animate={{ scale: 1 }} className="relative w-full max-w-sm rounded-3xl overflow-hidden" style={{ background: THEME.card, border: `3px solid ${THEME.neonPink}`, boxShadow: `0 0 50px ${THEME.neonPink}50` }}>
        <div className="p-8 text-center">
          <motion.div animate={{ scale: [1, 1.3, 1], rotate: [0, -15, 15, 0] }} transition={{ repeat: Infinity, duration: 0.5 }} className="w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${THEME.neonPink}, ${THEME.neonOrange})`, boxShadow: `0 0 40px ${THEME.neonPink}` }}><Bell className="w-12 h-12 text-white" /></motion.div>
          <h1 className="text-4xl font-black mb-2" style={{ color: THEME.neonPink }}>ALERT!</h1>
          <p className="text-xl text-white font-bold mb-2">{ping.message}</p>
          <p className="text-gray-400 text-sm mb-6">from {ping.sent_by}</p>
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={onAcknowledge} className="w-full py-4 rounded-xl font-black text-lg text-black" style={{ background: `linear-gradient(135deg, ${THEME.neonGreen}, ${THEME.neonCyan})` }}>I'M AWAKE!</motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============ EMPLOYEE PERFORMANCE TAB ============
function EmpCertSeal({ grade, score }) {
  const col = ({ 'A+': '#FFD700', 'A': '#00FF88', 'B': '#00F5FF', 'C': '#FF6B00', 'D': '#FF3366' })[grade] || '#C9A84C';
  return (
    <div className="relative w-[72px] h-[72px] flex-shrink-0">
      <svg viewBox="0 0 100 100" className="w-full h-full">
        {[...Array(12)].map((_, i) => {
          const angle = (i * 30) * Math.PI / 180;
          const r = i % 2 === 0 ? 44 : 36;
          return <line key={i} x1="50" y1="50" x2={50 + r * Math.cos(angle)} y2={50 + r * Math.sin(angle)} stroke={col} strokeWidth="1.5" opacity="0.3" />;
        })}
        <circle cx="50" cy="50" r="36" fill="none" stroke={col} strokeWidth="2" opacity="0.5" />
        <circle cx="50" cy="50" r="28" fill={`${col}15`} stroke={col} strokeWidth="1" />
        <circle cx="50" cy="50" r="24" fill="none" stroke={col} strokeWidth="2.5" strokeDasharray={`${score * 1.51} 151`} strokeLinecap="round" transform="rotate(-90 50 50)" opacity="0.8" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[18px] font-black leading-none" style={{ color: col }}>{score}</span>
        <span className="text-[7px] font-semibold" style={{ color: `${col}99` }}>SCORE</span>
      </div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

function ShiftCertificateCard({ cert, rank, totalEmployees, onExport, exporting, certRef }) {
  const gradeColor = ({ 'A+': '#FFD700', 'A': '#00FF88', 'B': '#00F5FF', 'C': '#FF6B00', 'D': '#FF3366' })[cert.grade] || '#C9A84C';

  const MetricCell = ({ label, value, color }) => (
    <div className="text-center py-1.5">
      <p className="text-[8px] uppercase tracking-widest mb-0.5" style={{ color: '#8A8A9A' }}>{label}</p>
      <p className="text-[14px] font-black leading-tight" style={{ color: color || '#E8E8F0' }}>{value}</p>
    </div>
  );

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onExport('download')} disabled={exporting}
          className="h-8 text-xs bg-gradient-to-r from-amber-600 to-yellow-500 text-black font-bold border-0" data-testid="emp-cert-download">
          <Download className="w-3.5 h-3.5 mr-1" /> Download
        </Button>
        <Button size="sm" onClick={() => onExport('copy')} disabled={exporting}
          className="h-8 text-xs bg-white/10 text-white border border-white/10" data-testid="emp-cert-copy">
          <Copy className="w-3.5 h-3.5 mr-1" /> Copy
        </Button>
      </div>

      <div ref={certRef} className="relative overflow-hidden rounded-xl" style={{
        background: 'linear-gradient(160deg, #0B0E18 0%, #111528 30%, #0D1020 60%, #0B0E18 100%)',
        padding: '20px',
      }}>
        <div className="absolute inset-[6px] rounded-lg border-2 pointer-events-none" style={{ borderColor: '#C9A84C50' }} />
        <div className="absolute inset-[12px] rounded-md border pointer-events-none" style={{ borderColor: '#C9A84C25', borderStyle: 'dashed' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[200px] rounded-full pointer-events-none"
          style={{ background: `radial-gradient(ellipse, ${gradeColor}08 0%, transparent 70%)` }} />

        <div className="relative z-10 space-y-3" style={{ padding: '8px' }}>
          {/* Title */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <div className="h-px flex-1 max-w-[50px]" style={{ background: `linear-gradient(to right, transparent, ${gradeColor}50)` }} />
              <span className="text-[8px] font-semibold uppercase tracking-[4px]" style={{ color: '#8A8A9A' }}>Certificate of</span>
              <div className="h-px flex-1 max-w-[50px]" style={{ background: `linear-gradient(to left, transparent, ${gradeColor}50)` }} />
            </div>
            <h1 className="text-[20px] font-black tracking-wide" style={{
              background: 'linear-gradient(135deg, #E8D5A3, #FFD700, #F0C050)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
            }}>PERFORMANCE</h1>
          </div>

          {/* Name + Seal */}
          <div className="flex items-center justify-center gap-4">
            <div className="text-center flex-1">
              <p className="text-[8px] uppercase tracking-[2px] mb-0.5" style={{ color: '#6B6B7B' }}>Awarded to</p>
              <h2 className="text-[20px] font-black text-white leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                {cert.employee_name}
              </h2>
              <div className="flex items-center justify-center gap-1.5 mt-1.5">
                <span className="px-2 py-0.5 rounded-full text-[9px] font-bold border" style={{ color: gradeColor, borderColor: `${gradeColor}50`, background: `${gradeColor}10` }}>
                  Grade {cert.grade}
                </span>
                <span className="px-2 py-0.5 rounded-full text-[9px] font-bold text-gray-300 bg-white/5 border border-white/10">
                  #{rank} of {totalEmployees}
                </span>
              </div>
            </div>
            <EmpCertSeal grade={cert.grade} score={cert.score} />
          </div>

          {/* Divider */}
          <div className="flex items-center gap-2">
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(to right, transparent, #FFD70025, transparent)' }} />
            <Award className="w-3 h-3" style={{ color: '#FFD70050' }} />
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(to left, transparent, #FFD70025, transparent)' }} />
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-4 gap-x-1">
            <MetricCell label="Deposits" value={formatCurrency(cert.total_deposit)} color="#00FF88" />
            <MetricCell label="Withdrawals" value={formatCurrency(cert.total_withdraw)} color="#FF3366" />
            <MetricCell label="Net" value={formatCurrency(cert.net_collection)} color="#FFD700" />
            <MetricCell label="Holding" value={`${cert.holding_pct}%`} color="#00F5FF" />
            <MetricCell label="New Players" value={cert.new_players} color="#9945FF" />
            <MetricCell label="Hours" value={`${cert.working_hours}h`} color="#E8E8F0" />
            <MetricCell label="Activity" value={`${cert.activity_score}/100`} color="#FF6B00" />
            <MetricCell label="Txns" value={cert.transactions} color="#00F5FF" />
          </div>

          {/* Shift Period Footer */}
          <div className="pt-2 mt-1" style={{ borderTop: '1px solid rgba(255,215,0,0.1)' }}>
            <p className="text-[7px] text-center uppercase tracking-[3px] mb-1.5" style={{ color: '#8A8A9A' }}>Certificate Period</p>
            <div className="flex items-center justify-between text-[9px]">
              <div>
                <span className="text-gray-500">Start: </span>
                <span className="text-white font-semibold">{fmtTime(cert.clock_in)}</span>
              </div>
              <span className="px-2 py-0.5 rounded-full text-[9px] font-black" style={{ background: `${gradeColor}15`, color: gradeColor }}>
                {cert.duration_hours}h
              </span>
              <div>
                <span className="text-gray-500">End: </span>
                <span className="text-white font-semibold">{fmtTime(cert.clock_out)}</span>
              </div>
            </div>
            <div className="flex items-center justify-center mt-2">
              <span className="text-[8px] font-bold tracking-widest" style={{ color: '#C9A84C70' }}>RIDER</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmployeePerformanceTab({ userId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('24h');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const certRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchCerts = async () => {
      setLoading(true);
      try {
        const res = await axios.get('/reports/shift-certificates', { params: { period } });
        setData(res.data);
        setSelectedIdx(0);
      } catch { }
      setLoading(false);
    };
    fetchCerts();
  }, [period]);

  const certs = data?.certificates || [];
  const selected = certs[selectedIdx];

  const doExport = async (action) => {
    if (!certRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(certRef.current, { backgroundColor: '#0B0E18', scale: 2, useCORS: true, logging: false });
      if (action === 'download') {
        const link = document.createElement('a');
        link.download = `shift_certificate_${selectedIdx + 1}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        toast.success('Downloaded!');
      } else {
        canvas.toBlob(async (blob) => {
          try { await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]); toast.success('Copied!'); } catch { toast.error('Copy failed'); }
        });
      }
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  return (
    <motion.div key="stats" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="p-4 space-y-3">
      {/* Period Toggle */}
      <div className="flex gap-2" data-testid="emp-period-selector">
        {[{ k: '24h', l: '24H' }, { k: '7d', l: '7D' }, { k: '30d', l: '1M' }].map(p => (
          <button key={p.k} onClick={() => setPeriod(p.k)}
            className={`px-4 py-2 rounded-xl text-sm font-bold ${period === p.k ? 'text-black' : 'bg-white/5 text-gray-400'}`}
            style={period === p.k ? { background: `linear-gradient(135deg, ${THEME.neonPurple}, ${THEME.neonCyan})` } : {}}
            data-testid={`emp-period-${p.k}`}
          >{p.l}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-10 h-10 rounded-full border-4 border-purple-500/30 border-t-purple-500 animate-spin" /></div>
      ) : certs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No completed shifts in this period</p>
          <p className="text-xs mt-1">Certificates are generated when you clock out</p>
        </div>
      ) : (
        <>
          {/* Current Certificate */}
          <ShiftCertificateCard
            cert={selected}
            rank={data.rank}
            totalEmployees={data.total_employees}
            onExport={doExport}
            exporting={exporting}
            certRef={certRef}
          />

          {/* Past Certificates History */}
          {certs.length > 1 && (
            <div className="pt-2" data-testid="past-certificates">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Past Certificates ({certs.length})</p>
              <div className="space-y-1.5">
                {certs.map((c, i) => {
                  const gc = ({ 'A+': '#FFD700', 'A': '#00FF88', 'B': '#00F5FF', 'C': '#FF6B00', 'D': '#FF3366' })[c.grade] || '#999';
                  const isActive = i === selectedIdx;
                  return (
                    <button key={c.shift_id} onClick={() => setSelectedIdx(i)}
                      className={`w-full p-3 rounded-xl text-left flex items-center gap-3 transition-all ${isActive ? 'ring-1' : 'hover:bg-white/5'}`}
                      style={{
                        background: isActive ? `${gc}10` : 'rgba(255,255,255,0.02)',
                        borderColor: isActive ? gc : 'transparent',
                        ringColor: isActive ? gc : undefined
                      }}
                      data-testid={`cert-history-${i}`}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black" style={{ background: `${gc}20`, color: gc }}>
                        {c.grade}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white font-semibold truncate">
                          {fmtTime(c.clock_in)} — {fmtTime(c.clock_out)}
                        </p>
                        <p className="text-[10px] text-gray-500">{c.duration_hours}h shift</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-mono font-bold" style={{ color: gc }}>{c.score}</p>
                        <p className="text-[10px] text-gray-500">score</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
}

// ============ MAIN EMPLOYEE APP ============
export default function EmployeeApp() {
  const { user, logout } = useAuth();
  
  // States
  const [activeTab, setActiveTab] = useState('home');
  const [shiftStatus, setShiftStatus] = useState('out');
  const [shiftTimer, setShiftTimer] = useState(0);
  const [shiftStartTime, setShiftStartTime] = useState(null);
  const [shiftEndTime, setShiftEndTime] = useState(null);
  const [myTransactions, setMyTransactions] = useState([]);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [wallets, setWallets] = useState([]);
  
  // Deposit form
  const [showDeposit, setShowDeposit] = useState(false);
  const [depositForm, setDepositForm] = useState({ player_name: '', game_name: '', deposit_amount: '', loaded_amount: '', payment_method: '' });
  const [players, setPlayers] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [games, setGames] = useState([]);
  
  // Performance
  const [avgWorkTime, setAvgWorkTime] = useState(0);
  
  // Modals
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    player_name: '',
    game_name: '',
    player_tag: '',
    win_amount: '',
    final_amount: '',
    payment_source: ''
  });
  const [showWalletOnWithdraw, setShowWalletOnWithdraw] = useState(false);
  
  // Overlays
  const [celebration, setCelebration] = useState(null);
  const [adminPing, setAdminPing] = useState(null);
  const [quickPopup, setQuickPopup] = useState(null);
  const [levelUp, setLevelUp] = useState(null);
  const [approvalNotification, setApprovalNotification] = useState(null);
  
  // Employee XP - Start at 0 for production
  const [stats, setStats] = useState(() => {
    const saved = localStorage.getItem(`emp_stats_${user?.id}`);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Reset to 0 if volume was old demo data
      if (parsed.volume === 450) return { xp: 0, level: 1, deposits: 0, volume: 0 };
      return parsed;
    }
    return { xp: 0, level: 1, deposits: 0, volume: 0 };
  });

  const refreshIntervalRef = useRef(null);

  // Save stats
  useEffect(() => { if (user?.id) localStorage.setItem(`emp_stats_${user.id}`, JSON.stringify(stats)); }, [stats, user?.id]);

  // XP calculation
  const getLevel = (xp) => Math.floor(xp / 500) + 1;
  const getCurrentLevelXp = (xp) => xp % 500;
  const getTierName = (level) => level >= 8 ? 'LEGENDARY' : level >= 5 ? 'EPIC' : 'WARRIOR';

  // Socket connections for real-time updates
  useEffect(() => {
    if (user?.id) {
      connectSocket(user.id);
      socket.emit('user_online', { user_id: user.id });
      socket.on('admin_ping', setAdminPing);
      socket.on('transaction_approved', handleTransactionApproved);
      socket.on('withdrawal_approved', () => {
        new Audio('/sounds/notify.wav').play().catch(() => {});
        fetchData();
      });
      socket.on('data_update', fetchData);
      return () => {
        socket.off('admin_ping');
        socket.off('transaction_approved');
        socket.off('withdrawal_approved');
        socket.off('data_update');
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleTransactionApproved = useCallback((data) => {
    new Audio('/sounds/notify.wav').play().catch(() => {});
    setApprovalNotification(data);
    fetchData();
  }, []);

  // Initial fetch + auto-refresh every 5 seconds
  useEffect(() => {
    fetchData();
    fetchWallets();
    checkShiftStatus();
    fetchShiftHistory();
    refreshIntervalRef.current = setInterval(fetchData, 5000);
    return () => clearInterval(refreshIntervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Shift timer
  useEffect(() => {
    let interval;
    if (shiftStatus === 'in' && shiftStartTime) {
      interval = setInterval(() => setShiftTimer(Math.floor((Date.now() - shiftStartTime) / 1000)), 1000);
    }
    return () => clearInterval(interval);
  }, [shiftStatus, shiftStartTime]);

  // API calls
  const fetchPlayers = async () => {
    setPlayersLoading(true);
    try {
      const res = await axios.get('/players');
      const list = Array.isArray(res.data) ? res.data : res.data?.players || [];
      setPlayers(list);
    } catch (e) { /* keep existing players */ }
    setPlayersLoading(false);
  };

  const fetchData = async () => {
    try {
      const [transRes, playersRes, gamesRes] = await Promise.all([
        axios.get('/transactions?limit=20'),
        axios.get('/players'),
        axios.get('/games')
      ]);
      const allTrans = transRes.data || [];
      setMyTransactions(allTrans.filter(t => t.status === 'approved'));
      setPendingTransactions(allTrans.filter(t => t.status === 'pending'));
      setPlayers(Array.isArray(playersRes.data) ? playersRes.data : playersRes.data?.players || []);
      setGames(gamesRes.data || []);
    } catch (e) {}
  };
  const fetchWallets = async () => {
    try {
      const [walletRes, settingsRes] = await Promise.all([
        axios.get('/wallets'),
        axios.get('/admin/settings').catch(() => ({ data: {} }))
      ]);
      const walletList = walletRes.data.wallets || [];
      setWallets(walletList);
      setShowWalletOnWithdraw(settingsRes.data.show_wallet_on_withdraw || false);
      if (walletList.length > 0 && !depositForm.payment_method) {
        setDepositForm(prev => ({ ...prev, payment_method: `${walletList[0].wallet_name}${walletList[0].tag_name ? ' - ' + walletList[0].tag_name : ''}` }));
      }
    } catch (e) { setWallets([]); }
  };

  const fetchShiftHistory = async () => {
    try {
      const res = await axios.get('/shifts');
      const history = (res.data || []).filter(s => s.status === 'completed');
      if (history.length > 0) {
        const totalMinutes = history.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
        setAvgWorkTime(Math.round(totalMinutes / history.length));
      }
    } catch (e) {}
  };

  const checkShiftStatus = async () => {
    try {
      const res = await axios.get('/shifts');
      const activeShift = res.data?.find(s => s.status === 'active');
      if (activeShift) {
        setShiftStatus('in');
        setShiftStartTime(new Date(activeShift.clock_in).getTime());
      }
    } catch (e) { setShiftStatus('out'); }
  };

  const handleClockInOut = async () => {
    try {
      if (shiftStatus === 'in') {
        await axios.post('/shifts', { action: 'out' });
        setShiftStatus('out');
        setShiftEndTime(Date.now());
        setShiftTimer(0);
        setShiftStartTime(null);
        setQuickPopup({ message: "BYE BYE!", icon: LogOut, color: THEME.neonPink });
        fetchShiftHistory();
      } else {
        await axios.post('/shifts', { action: 'in' });
        setShiftStatus('in');
        setShiftStartTime(Date.now());
        setShiftEndTime(null);
        setQuickPopup({ message: "LET'S GO!", icon: Play, color: THEME.neonGreen });
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    await submitDeposit();
  };

  const createPlayerInline = async (name) => {
    try {
      const res = await axios.post('/players', {
        name,
        page_id: user?.assigned_page_id || null
      });
      const newPlayer = res.data;
      await fetchPlayers();
      toast.success(`Player "${name}" created`);
      return newPlayer;
    } catch (e) {
      if (e.response?.status === 400) toast.error(e.response.data.detail);
      else toast.error('Failed to create player');
      return null;
    }
  };

  // Selected player state for deposit/withdraw
  const [depositSelectedPlayer, setDepositSelectedPlayer] = useState(null);
  const [withdrawSelectedPlayer, setWithdrawSelectedPlayer] = useState(null);

  const submitDeposit = async () => {
    try {
      const player = depositSelectedPlayer || players.find(p => p.name.toLowerCase() === depositForm.player_name.toLowerCase());
      const amount = parseFloat(depositForm.deposit_amount) || 0;
      const oldLevel = stats.level;
      
      await axios.post('/transactions', {
        player_id: player?.id || '',
        player_name: depositForm.player_name,
        game_name: depositForm.game_name,
        transaction_type: 'deposit',
        deposit_amount: amount,
        loaded_amount: parseFloat(depositForm.loaded_amount) || 0,
        payment_source: depositForm.payment_method,
        bonus: 0
      });
      
      const xpGained = Math.floor(amount / 10) + 25;
      const newXp = stats.xp + xpGained;
      const newLevel = getLevel(newXp);
      
      setStats({ xp: newXp, level: newLevel, deposits: stats.deposits + 1, volume: stats.volume + amount });
      
      if (newLevel > oldLevel) setTimeout(() => setLevelUp({ oldLevel, newLevel, tierName: getTierName(newLevel) }), 3500);
      
      setCelebration({ amount, playerName: depositForm.player_name, xpGained });
      setDepositForm({ player_name: '', game_name: '', deposit_amount: '', loaded_amount: '', payment_method: wallets.length > 0 ? `${wallets[0].wallet_name}${wallets[0].tag_name ? ' - ' + wallets[0].tag_name : ''}` : '' });
      setDepositSelectedPlayer(null);
      setShowDeposit(false);
      fetchData();
    } catch (e) { toast.error('Failed to process deposit'); }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    try {
      const player = withdrawSelectedPlayer || players.find(p => p.name.toLowerCase() === withdrawForm.player_name.toLowerCase());
      await axios.post('/withdrawals', {
        player_name: withdrawForm.player_name,
        player_id: player?.id || '',
        game_name: withdrawForm.game_name,
        player_tag: withdrawForm.player_tag || null,
        win_amount: parseFloat(withdrawForm.win_amount) || 0,
        final_amount: parseFloat(withdrawForm.final_amount),
        payment_source: withdrawForm.payment_source || null
      });
      toast.success('Withdrawal request submitted!');
      setWithdrawForm({ player_name: '', game_name: '', player_tag: '', win_amount: '', final_amount: '', payment_source: '' });
      setWithdrawSelectedPlayer(null);
      setShowWithdraw(false);
      fetchData();
    } catch (e) { toast.error('Failed to submit withdrawal'); }
  };

  const acknowledgePing = async () => {
    if (!adminPing) return;
    try { await axios.post(`/pings/${adminPing.id}/acknowledge`); setAdminPing(null); } catch (e) {}
  };

  const handleLogout = () => {
    setQuickPopup({ message: "SEE YOU!", icon: LogOut, color: THEME.neonPink });
    setTimeout(() => {
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem(`emp_stats_${user?.id}`);
      // Call logout from auth context
      logout();
      // Force redirect to login
      window.location.href = '/login';
    }, 1200);
  };

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  const formatTimeOfDay = (timestamp) => {
    if (!timestamp) return '--:--';
    return new Date(timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const paymentOptions = wallets.length > 0 ? wallets.filter(w => !w.is_hidden).map(w => `${w.wallet_name}${w.tag_name ? ' - ' + w.tag_name : ''}`) : ['Cash App', 'Crypto', 'Bank', 'Cash'];

  return (
    <div className="min-h-screen relative" style={{ background: THEME.bg }}>
      <GamingBackground />

      {/* HEADER */}
      <header className="sticky top-0 z-50 px-4 py-3 backdrop-blur-xl" style={{ background: 'rgba(5,5,8,0.9)', borderBottom: `1px solid ${THEME.cardBorder}` }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <motion.div animate={{ rotate: [0, 5, -5, 0] }} transition={{ repeat: Infinity, duration: 3 }} className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${THEME.neonPurple}, ${THEME.neonCyan})` }}><Gamepad2 className="w-5 h-5 text-white" /></motion.div>
            <h1 className="text-lg font-black"><span className="bg-gradient-to-r from-[#FFD700] to-[#00F5FF] bg-clip-text text-transparent">RIDER</span></h1>
          </div>
          <div className="flex items-center gap-2">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => toast.info('Contacting admin...')} className="p-2.5 rounded-xl" style={{ background: `${THEME.neonPink}15`, border: `1px solid ${THEME.neonPink}30` }} data-testid="admin-btn"><ShieldAlert className="w-5 h-5" style={{ color: THEME.neonPink }} /></motion.button>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={handleLogout} className="p-2.5 rounded-xl" style={{ background: `${THEME.neonOrange}15`, border: `1px solid ${THEME.neonOrange}30` }} data-testid="logout-btn"><LogOut className="w-5 h-5" style={{ color: THEME.neonOrange }} /></motion.button>
          </div>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main className="relative z-10 pb-24">
        <AnimatePresence mode="wait">
          {activeTab === 'home' && (
            <motion.div key="home" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="p-4 space-y-4">
              
              {/* Clickable Pending Banner */}
              {pendingTransactions.length > 0 && (
                <motion.button initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} onClick={() => setShowPendingModal(true)} className="w-full p-3 rounded-xl flex items-center gap-3" style={{ background: `${THEME.neonOrange}20`, border: `1px solid ${THEME.neonOrange}50` }}>
                  <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1 }}><AlertCircle className="w-5 h-5" style={{ color: THEME.neonOrange }} /></motion.div>
                  <div className="flex-1 text-left"><p className="text-sm font-bold text-white">{pendingTransactions.length} PENDING</p><p className="text-xs text-gray-400">Tap to see details</p></div>
                  <Badge style={{ background: `${THEME.neonOrange}30`, color: THEME.neonOrange }}>{pendingTransactions.length}</Badge>
                  <ChevronRight className="w-4 h-4" style={{ color: THEME.neonOrange }} />
                </motion.button>
              )}

              {/* Level Badge */}
              <div className="flex justify-center"><LevelBadge level={stats.level} xp={getCurrentLevelXp(stats.xp)} maxXp={500} /></div>

              {/* Battle Status */}
              <Card className="p-5 rounded-2xl border" style={{ background: THEME.card, borderColor: THEME.cardBorder }}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Battle Status</p>
                    <p className="text-3xl font-black text-white mt-1">{shiftStatus === 'in' ? formatTime(shiftTimer) : 'READY'}</p>
                    {shiftStatus === 'in' && (<div className="flex items-center gap-2 mt-1"><motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1 }} className="w-2 h-2 rounded-full" style={{ background: THEME.neonGreen }} /><span className="text-xs" style={{ color: THEME.neonGreen }}>ON DUTY</span></div>)}
                  </div>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleClockInOut} className="px-6 py-3 rounded-xl font-black text-black" style={{ background: shiftStatus === 'in' ? `linear-gradient(135deg, ${THEME.neonPink}, ${THEME.neonOrange})` : `linear-gradient(135deg, ${THEME.neonGreen}, ${THEME.neonCyan})` }} data-testid="clock-btn">{shiftStatus === 'in' ? 'END' : 'START'}</motion.button>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-4" style={{ borderTop: `1px solid ${THEME.cardBorder}` }}>
                  <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(0,0,0,0.3)' }}><p className="text-xs text-gray-500">Started</p><p className="text-lg font-bold" style={{ color: THEME.neonGreen }}>{shiftStartTime ? formatTimeOfDay(shiftStartTime) : '--:--'}</p></div>
                  <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(0,0,0,0.3)' }}><p className="text-xs text-gray-500">Ended</p><p className="text-lg font-bold" style={{ color: THEME.neonPink }}>{shiftStatus === 'out' && shiftEndTime ? formatTimeOfDay(shiftEndTime) : '--:--'}</p></div>
                </div>
              </Card>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3">
                <StatCard icon={Coins} label="Volume" value={formatCurrency(stats.volume)} color={THEME.neonGold} />
                <StatCard icon={Swords} label="Battles" value={stats.deposits} color={THEME.neonCyan} />
                <StatCard icon={Zap} label="XP" value={stats.xp} color={THEME.neonPurple} />
                <StatCard icon={Trophy} label="Rank" value="#" suffix="1" color={THEME.neonGreen} />
              </div>

              {/* Performance */}
              <Card className="p-4 rounded-2xl border" style={{ background: THEME.card, borderColor: THEME.cardBorder }}>
                <div className="flex items-center gap-2 mb-3"><Activity className="w-5 h-5" style={{ color: THEME.neonPurple }} /><span className="font-bold text-white">Performance</span></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl text-center" style={{ background: `${THEME.neonCyan}10` }}><Timer className="w-5 h-5 mx-auto mb-1" style={{ color: THEME.neonCyan }} /><p className="text-xs text-gray-500">Avg. Work Time</p><p className="text-lg font-black" style={{ color: THEME.neonCyan }}>{Math.floor(avgWorkTime / 60)}h {avgWorkTime % 60}m</p></div>
                  <div className="p-3 rounded-xl text-center" style={{ background: `${THEME.neonGold}10` }}><Flame className="w-5 h-5 mx-auto mb-1" style={{ color: THEME.neonGold }} /><p className="text-xs text-gray-500">Today's Volume</p><p className="text-lg font-black" style={{ color: THEME.neonGold }}>{formatCurrency(stats.volume)}</p></div>
                </div>
              </Card>

              {/* Recent Battles */}
              <Card className="rounded-2xl border overflow-hidden" style={{ background: THEME.card, borderColor: THEME.cardBorder }}>
                <div className="px-5 py-4 flex items-center gap-2" style={{ borderBottom: `1px solid ${THEME.cardBorder}` }}><Swords className="w-5 h-5" style={{ color: THEME.neonPurple }} /><span className="font-bold text-white">Recent Battles</span></div>
                <div className="divide-y" style={{ borderColor: THEME.cardBorder }}>
                  {myTransactions.slice(0, 5).map((txn, idx) => (
                    <motion.div key={txn.id || idx} className="px-5 py-3 flex items-center justify-between" whileHover={{ background: 'rgba(255,255,255,0.05)' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${THEME.neonGreen}20` }}><TrendingUp className="w-5 h-5" style={{ color: THEME.neonGreen }} /></div>
                        <div><p className="font-semibold text-white text-sm">{txn.player_name}</p><p className="text-xs text-gray-500">{txn.payment_source || txn.game_name}</p></div>
                      </div>
                      <div className="text-right"><p className="font-black" style={{ color: THEME.neonGreen }}>+{formatCurrency(txn.deposit_amount || txn.amount)}</p><Badge className="text-[10px]" style={{ background: `${THEME.neonGreen}20`, color: THEME.neonGreen }}><Check className="w-3 h-3 mr-1" /> Approved</Badge></div>
                    </motion.div>
                  ))}
                  {myTransactions.length === 0 && <div className="p-8 text-center text-gray-500">No battles yet</div>}
                </div>
              </Card>
            </motion.div>
          )}

          {activeTab === 'stats' && (
            <EmployeePerformanceTab userId={user?.id} />
          )}
        </AnimatePresence>
      </main>

      {/* BOTTOM NAV */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 px-4 py-3 backdrop-blur-xl" style={{ background: 'rgba(5,5,8,0.95)', borderTop: `1px solid ${THEME.cardBorder}` }}>
        <div className="flex items-center justify-around max-w-md mx-auto">
          <button onClick={() => setActiveTab('home')} className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all" style={{ background: activeTab === 'home' ? `${THEME.neonCyan}20` : 'transparent' }} data-testid="nav-home">
            <Home className="w-6 h-6" style={{ color: activeTab === 'home' ? THEME.neonCyan : '#666' }} /><span className="text-xs font-bold" style={{ color: activeTab === 'home' ? THEME.neonCyan : '#666' }}>Home</span>
          </button>
          <div className="flex items-center gap-2 -mt-6">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setShowDeposit(true)} className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${THEME.neonGreen}, ${THEME.neonCyan})`, boxShadow: `0 0 20px ${THEME.neonGreen}60` }} data-testid="nav-deposit"><Plus className="w-7 h-7 text-black" /></motion.button>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setShowWithdraw(true)} className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${THEME.neonPink}, ${THEME.neonOrange})`, boxShadow: `0 0 20px ${THEME.neonPink}60` }} data-testid="nav-withdraw"><Minus className="w-7 h-7 text-white" /></motion.button>
          </div>
          <button onClick={() => setActiveTab('stats')} className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all" style={{ background: activeTab === 'stats' ? `${THEME.neonGold}20` : 'transparent' }} data-testid="nav-stats">
            <Trophy className="w-6 h-6" style={{ color: activeTab === 'stats' ? THEME.neonGold : '#666' }} /><span className="text-xs font-bold" style={{ color: activeTab === 'stats' ? THEME.neonGold : '#666' }}>Stats</span>
          </button>
        </div>
      </nav>
      {/* DEPOSIT MODAL */}
      <AnimatePresence>
        {showDeposit && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-end justify-center" onClick={() => setShowDeposit(false)}>
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 25 }} onClick={(e) => e.stopPropagation()} className="relative w-full max-w-md rounded-t-3xl overflow-hidden" style={{ background: THEME.card, border: `2px solid ${THEME.neonGreen}50`, borderBottom: 'none' }}>
              <div className="p-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${THEME.cardBorder}` }}>
                <div className="flex items-center gap-3"><div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${THEME.neonGreen}, ${THEME.neonCyan})` }}><Plus className="w-6 h-6 text-black" /></div><div><h2 className="text-xl font-black text-white">NEW DEPOSIT</h2><p className="text-xs text-gray-500">Process player deposit</p></div></div>
                <button onClick={() => setShowDeposit(false)} className="p-2 rounded-lg hover:bg-white/10"><X className="w-5 h-5 text-gray-400" /></button>
              </div>
              <form onSubmit={handleDeposit} className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <Label className="text-xs text-gray-400 flex items-center gap-1 mb-2"><User className="w-3 h-3" /> PLAYER NAME</Label>
                  <PlayerSearchInput
                    value={depositForm.player_name}
                    onChange={(val) => setDepositForm({ ...depositForm, player_name: val })}
                    onPlayerSelect={(p) => { setDepositSelectedPlayer(p); setDepositForm(f => ({ ...f, player_name: p.name })); }}
                    players={players}
                    pageId={user?.assigned_page_id}
                    onCreatePlayer={createPlayerInline}
                    onOpen={fetchPlayers}
                    loading={playersLoading}
                    placeholder="Search or create player..."
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-400 flex items-center gap-1 mb-2"><Gamepad2 className="w-3 h-3" /> GAME</Label>
                  <select value={depositForm.game_name} onChange={(e) => setDepositForm({ ...depositForm, game_name: e.target.value })} required className="w-full h-12 rounded-xl border-0 px-4" style={{ background: 'rgba(0,0,0,0.3)', color: 'white' }}><option value="">Select game</option>{games.map(g => <option key={g.id} value={g.name}>{g.name}</option>)}</select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-gray-400 flex items-center gap-1 mb-2"><DollarSign className="w-3 h-3" /> DEPOSIT</Label><Input type="number" value={depositForm.deposit_amount} onChange={(e) => setDepositForm({ ...depositForm, deposit_amount: e.target.value })} placeholder="0.00" required className="h-12 rounded-xl border-0 text-lg font-bold" style={{ background: 'rgba(0,0,0,0.3)', color: THEME.neonGreen }} /></div>
                  <div><Label className="text-xs text-gray-400 flex items-center gap-1 mb-2"><Wallet className="w-3 h-3" /> LOADED</Label><Input type="number" value={depositForm.loaded_amount} onChange={(e) => setDepositForm({ ...depositForm, loaded_amount: e.target.value })} placeholder="0.00" required className="h-12 rounded-xl border-0 text-lg font-bold" style={{ background: 'rgba(0,0,0,0.3)', color: THEME.neonGold }} /></div>
                </div>
                <div>
                  <Label className="text-xs text-gray-400 flex items-center gap-1 mb-2"><Wallet className="w-3 h-3" /> PAYMENT METHOD</Label>
                  <div className="grid grid-cols-2 gap-2">{paymentOptions.map(method => (<button key={method} type="button" onClick={() => setDepositForm({ ...depositForm, payment_method: method })} className="p-3 rounded-xl text-xs font-bold transition-all truncate" style={{ background: depositForm.payment_method === method ? `${THEME.neonCyan}30` : 'rgba(0,0,0,0.3)', border: `1px solid ${depositForm.payment_method === method ? THEME.neonCyan : 'transparent'}`, color: depositForm.payment_method === method ? THEME.neonCyan : '#888' }}>{method}</button>))}</div>
                </div>
                <motion.button type="submit" whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }} className="w-full h-14 rounded-xl font-black text-lg text-black mt-4" style={{ background: `linear-gradient(135deg, ${THEME.neonGreen}, ${THEME.neonCyan})` }} data-testid="submit-deposit">PROCESS DEPOSIT</motion.button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WITHDRAW MODAL */}
      <AnimatePresence>
        {showWithdraw && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-end justify-center" onClick={() => setShowWithdraw(false)}>
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 25 }} onClick={(e) => e.stopPropagation()} className="relative w-full max-w-md rounded-t-3xl overflow-hidden" style={{ background: THEME.card, border: `2px solid ${THEME.neonPink}50`, borderBottom: 'none' }}>
              <div className="p-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${THEME.cardBorder}` }}>
                <div className="flex items-center gap-3"><div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${THEME.neonPink}, ${THEME.neonOrange})` }}><ArrowDownRight className="w-6 h-6 text-white" /></div><div><h2 className="text-xl font-black text-white">WITHDRAW REQUEST</h2><p className="text-xs text-gray-500">Process player withdrawal</p></div></div>
                <button onClick={() => setShowWithdraw(false)} className="p-2 rounded-lg hover:bg-white/10"><X className="w-5 h-5 text-gray-400" /></button>
              </div>
              <form onSubmit={handleWithdraw} className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <Label className="text-xs text-gray-400 flex items-center gap-1 mb-2"><User className="w-3 h-3" /> PLAYER NAME</Label>
                  <PlayerSearchInput
                    value={withdrawForm.player_name}
                    onChange={(val) => setWithdrawForm({ ...withdrawForm, player_name: val })}
                    onPlayerSelect={(p) => { setWithdrawSelectedPlayer(p); setWithdrawForm(f => ({ ...f, player_name: p.name })); }}
                    players={players}
                    pageId={user?.assigned_page_id}
                    onCreatePlayer={createPlayerInline}
                    onOpen={fetchPlayers}
                    loading={playersLoading}
                    placeholder="Search player..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-400 flex items-center gap-1 mb-2"><Gamepad2 className="w-3 h-3" /> GAME</Label>
                    <select value={withdrawForm.game_name} onChange={(e) => setWithdrawForm({ ...withdrawForm, game_name: e.target.value })} required className="w-full h-12 rounded-xl border-0 px-4" style={{ background: 'rgba(0,0,0,0.3)', color: 'white' }}><option value="">Select game</option>{games.map(g => <option key={g.id} value={g.name}>{g.name}</option>)}</select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400 flex items-center gap-1 mb-2"><Tag className="w-3 h-3" /> TAG NAME</Label>
                    <Input value={withdrawForm.player_tag} onChange={(e) => setWithdrawForm({ ...withdrawForm, player_tag: e.target.value })} placeholder="Optional" className="h-12 rounded-xl border-0" style={{ background: 'rgba(0,0,0,0.3)', color: 'white' }} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-400 flex items-center gap-1 mb-2"><Trophy className="w-3 h-3" /> WIN AMOUNT</Label>
                    <Input type="number" value={withdrawForm.win_amount} onChange={(e) => setWithdrawForm({ ...withdrawForm, win_amount: e.target.value })} placeholder="Optional" className="h-12 rounded-xl border-0 text-lg font-bold" style={{ background: 'rgba(0,0,0,0.3)', color: THEME.neonGold }} />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400 flex items-center gap-1 mb-2"><DollarSign className="w-3 h-3" /> FINAL AMOUNT *</Label>
                    <Input type="number" value={withdrawForm.final_amount} onChange={(e) => setWithdrawForm({ ...withdrawForm, final_amount: e.target.value })} placeholder="0.00" required className="h-12 rounded-xl border-0 text-lg font-bold" style={{ background: 'rgba(0,0,0,0.3)', color: THEME.neonPink }} />
                  </div>
                </div>
                {showWalletOnWithdraw && wallets.length > 0 && (
                  <div>
                    <Label className="text-xs text-gray-400 flex items-center gap-1 mb-2"><Wallet className="w-3 h-3" /> WALLET</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {wallets.filter(w => !w.is_hidden).map(w => {
                        const label = `${w.wallet_name}${w.tag_name ? ' - ' + w.tag_name : ''}`;
                        return (
                          <button key={w.id} type="button" onClick={() => setWithdrawForm({ ...withdrawForm, payment_source: label })} className="p-3 rounded-xl text-xs font-bold transition-all truncate" style={{ background: withdrawForm.payment_source === label ? `${THEME.neonPink}30` : 'rgba(0,0,0,0.3)', border: `1px solid ${withdrawForm.payment_source === label ? THEME.neonPink : 'transparent'}`, color: withdrawForm.payment_source === label ? THEME.neonPink : '#888' }} data-testid={`withdraw-wallet-${w.id}`}>{label}</button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <motion.button type="submit" whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }} className="w-full h-14 rounded-xl font-black text-lg text-white mt-4" style={{ background: `linear-gradient(135deg, ${THEME.neonPink}, ${THEME.neonOrange})` }} data-testid="submit-withdraw">SUBMIT WITHDRAWAL</motion.button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PENDING ORDERS MODAL */}
      <AnimatePresence>{showPendingModal && <PendingOrdersModal orders={pendingTransactions} onClose={() => setShowPendingModal(false)} />}</AnimatePresence>

      {/* ADD PLAYER MODAL */}
      <AnimatePresence>{showAddPlayer && <AddPlayerModal initialName={newPlayerName} onClose={() => setShowAddPlayer(false)} onSuccess={(name) => { fetchData(); setDepositForm(prev => ({ ...prev, player_name: name })); }} />}</AnimatePresence>

      {/* OVERLAYS */}
      <AnimatePresence>{quickPopup && <QuickPopup {...quickPopup} onClose={() => setQuickPopup(null)} />}</AnimatePresence>
      <AnimatePresence>{celebration && <CelebrationOverlay amount={celebration.amount} playerName={celebration.playerName} xpGained={celebration.xpGained} onClose={() => setCelebration(null)} />}</AnimatePresence>
      <AnimatePresence>{levelUp && <LevelUpOverlay oldLevel={levelUp.oldLevel} newLevel={levelUp.newLevel} tierName={levelUp.tierName} onClose={() => setLevelUp(null)} />}</AnimatePresence>
      <AnimatePresence>{approvalNotification && <ApprovalNotification transaction={approvalNotification} onClose={() => setApprovalNotification(null)} />}</AnimatePresence>
      <AnimatePresence>{adminPing && <AdminPingAlert ping={adminPing} onAcknowledge={acknowledgePing} />}</AnimatePresence>
    </div>
  );
}
